import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Polls a dev-rail read while its panel is open.
 *
 * Same shape as `useGameweekPhasePoll`: the loader is read through a ref refreshed on every
 * render, so the interval is registered once per open rather than torn down and recreated on each
 * render — giving the effect real dependencies is how a poll ends up never firing.
 *
 * It stops when the panel closes and on unmount. These reads cost nothing on the provider side
 * (they hit our own tables, or a replica on localhost), but an interval still running behind a
 * closed panel is a leak, and the test for this ticket asserts it does not happen.
 */
export interface DevPoll<T> {
  data: T | null
  error: string | null
  /**
   * When `data` arrived. Relative times in the panel are measured from here rather than from a
   * clock read during render: the panel only changes when a poll lands, so the fetch instant is
   * both the honest reference point and a pure one.
   */
  receivedAtMs: number | null
  /** Re-read immediately — used after an action that changes what the panel shows. */
  refresh: () => void
}

export function useDevPoll<T>(
  load: () => Promise<T>,
  options: { enabled: boolean; intervalMs: number }
): DevPoll<T> {
  const { enabled, intervalMs } = options
  const [state, setState] = useState<{
    data: T | null
    error: string | null
    receivedAtMs: number | null
  }>({ data: null, error: null, receivedAtMs: null })
  const latest = useRef(load)
  useEffect(() => {
    latest.current = load
  })

  // Guards a response arriving after the panel closed, which would otherwise set state on a
  // component that is no longer polling — and, worse, show a stale reading next time it opens.
  const generation = useRef(0)

  const read = useCallback(async () => {
    const mine = generation.current
    try {
      const data = await latest.current()
      if (generation.current !== mine) return
      setState({ data, error: null, receivedAtMs: Date.now() })
    } catch (failure) {
      if (generation.current !== mine) return
      setState((previous) => ({
        ...previous,
        error: failure instanceof Error ? failure.message : String(failure),
      }))
    }
  }, [])

  useEffect(() => {
    if (!enabled) return
    void read()
    const id = setInterval(() => void read(), intervalMs)
    return () => {
      generation.current += 1
      clearInterval(id)
    }
  }, [enabled, intervalMs, read])

  return { ...state, refresh: () => void read() }
}
